import {
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import SearchBar from "../../components/SearchBar";
import { useGetSubscriptions, useGetSubscriptionSettings, useUpdateSubscriptionSettings } from "../../api/hooks/useSubscription";
import { showError, showSuccess } from "../../helpers/toast";
import type { Subscription } from "../../api/types/subscription.types";
import Pagination from "../../components/Panigation";
import SettingsIcon from "@mui/icons-material/Settings";

const Subscriptions = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [freeTrialPeriod, setFreeTrialPeriod] = useState<string | number>("");

    // Subscription Settings Hooks
    const { data: settingsData, isLoading: isLoadingSettings } = useGetSubscriptionSettings({
        enabled: isSettingsOpen
    });

    const { mutate: updateSettings, isPending: isUpdatingSettings } = useUpdateSubscriptionSettings({
        onSuccess: () => {
            showSuccess("Settings updated successfully");
            setIsSettingsOpen(false);
        },
        onError: (error: any) => {
            showError(error?.response?.data?.message || "Failed to update settings");
        }
    });

    // Update freeTrialPeriod when settings are fetched
    useEffect(() => {
        if (settingsData?.data) {
            setFreeTrialPeriod(settingsData.data.days);
        }
    }, [settingsData]);

    // Debounce search term
    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setPage(1); // Reset to page 1 when search changes
        }, 500);

        return () => {
            clearTimeout(timerId);
        };
    }, [searchTerm]);

    // Fetch subscriptions
    const { data: subscriptionsData, isLoading: isLoadingSubscriptions, refetch: refetchSubscriptions, error: subscriptionsError } = useGetSubscriptions(
        {
            ...(debouncedSearchTerm.trim() && { search: debouncedSearchTerm.trim() }),
            page,
            limit,
            offset: (page - 1) * limit,
        },
        { enabled: true }
    );

    // Extract subscriptions and totalCount from response
    const { subscriptions, totalCount } = useMemo(() => {
        const data = subscriptionsData?.data;
        let subscriptionsList: Subscription[] = [];
        let total = 0;

        if (Array.isArray(data)) {
            subscriptionsList = data;
            total = data.length;
        } else if (data && typeof data === 'object') {
            const dataObj = data as any;
            if ('list' in dataObj && Array.isArray(dataObj.list)) {
                subscriptionsList = dataObj.list;
                total = dataObj.totalCount || dataObj.list.length;
            } else if ('data' in dataObj && Array.isArray(dataObj.data)) {
                subscriptionsList = dataObj.data;
                total = dataObj.totalCount || dataObj.data.length;
            } else if (dataObj.totalCount !== undefined) {
                total = dataObj.totalCount;
            }
        }

        if (!Array.isArray(subscriptionsList)) {
            subscriptionsList = [];
        }

        return {
            subscriptions: subscriptionsList,
            totalCount: total || subscriptionsList.length,
        };
    }, [subscriptionsData]);

    useEffect(() => {
        if (subscriptionsError) {
            showError((subscriptionsError as any)?.response?.data?.message || "Failed to load subscriptions");
        }
    }, [subscriptionsError]);

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        } catch {
            return "N/A";
        }
    };

    const formatPrice = (price: number) => {
        if (price === 0) return "Free";
        return `$${price.toFixed(2)}`;
    };

    const formatValidity = (validity: number) => {
        if (!validity) return "N/A";
        if (validity === 365) return "1 Year";
        if (validity === 30) return "1 Month";
        if (validity < 30) return `${validity} Days`;
        if (validity < 365) {
            const months = Math.floor(validity / 30);
            return `${months} Month${months > 1 ? 's' : ''}`;
        }
        const years = Math.floor(validity / 365);
        return `${years} Year${years > 1 ? 's' : ''}`;
    };

    const formatBenefits = (benefits: string) => {
        if (!benefits) return [];
        // Split by comma or newline
        return benefits.split(/[,\n]/).map(b => b.trim()).filter(b => b.length > 0);
    };

    const handleEditSubscription = (subscription: Subscription) => {
        navigate("/subscription/edit", { state: { subscriptionId: subscription._id } });
    };

    const handleViewSubscription = (subscription: Subscription) => {
        navigate("/subscription/view", { state: { subscriptionId: subscription._id } });
    };

    const totalPages = Math.ceil(totalCount / limit);
    const onPageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    return (
        <>
            <div className="main_title">
                <h1>Manage Subscriptions</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Manage Subscriptions
                </p>
            </div>

            <div className="cards">
                <div className="table_header">
                    <div className="left_s" style={{ width: "100%" }}>
                        <Box display="flex" alignItems="center" gap={1} width="100%">
                            <SearchBar
                                value={searchTerm}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                                onCross={() => setSearchTerm("")}
                                searchTerm={searchTerm}
                                setDebouncedSearchTerm={setSearchTerm}
                                placeholder="Search Subscription Plans..."
                            />
                            <IconButton
                                aria-label="settings"
                                onClick={() => setIsSettingsOpen(true)}
                                title="Settings"
                            >
                                <SettingsIcon />
                            </IconButton>
                        </Box>
                    </div>
                </div>

                <TableContainer className="table_container">
                    {isLoadingSubscriptions ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : subscriptionsError ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                            <p>Error loading subscriptions. Please try again.</p>
                        </Box>
                    ) : subscriptions.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                            <p>No subscriptions found.</p>
                        </Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sr. No.</TableCell>
                                    <TableCell>Subscription Type</TableCell>
                                    <TableCell>Validity</TableCell>
                                    <TableCell>Price</TableCell>
                                    <TableCell>Benefits</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {subscriptions.map((subscription: Subscription, index: number) => {
                                    const benefits = formatBenefits(subscription.benefits || "");

                                    return (
                                        <TableRow hover key={subscription._id}>
                                            <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                                            <TableCell>{subscription.name || "N/A"}</TableCell>
                                            <TableCell>{formatValidity(subscription.validity)}</TableCell>
                                            <TableCell>{formatPrice(subscription.price)}</TableCell>
                                            <TableCell>
                                                {benefits.length > 0 ? (
                                                    benefits.map((benefit, i) => (
                                                        <div key={i}>• {benefit}</div>
                                                    ))
                                                ) : (
                                                    "N/A"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="table_actions v2">
                                                    <IconButton
                                                        onClick={() => handleEditSubscription(subscription)}
                                                        disabled={isLoadingSubscriptions}
                                                        title="Edit Subscription"
                                                    >
                                                        <img src="/static/images/edit2_icon.svg" alt="Edit" />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => handleViewSubscription(subscription)}
                                                        disabled={isLoadingSubscriptions}
                                                        title="View Subscription"
                                                    >
                                                        <img src="/static/images/eye_icon.svg" alt="View" />
                                                    </IconButton>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>

                {/* Pagination */}
                {!isLoadingSubscriptions && !subscriptionsError && totalCount > 0 && (
                    <Pagination
                        module={subscriptions}
                        page={page}
                        onPageChange={onPageChange}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        setPage={setPage}
                        limit={limit}
                    />
                )}
            </div>

            <Dialog open={isSettingsOpen} onClose={() => setIsSettingsOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Settings</DialogTitle>
                <DialogContent>
                    <div className="form">
                        {isLoadingSettings ? (
                            <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <div className="control_group w_100">
                                <label htmlFor="free-trial-period">Free trial period (Days)</label>
                                <TextField
                                    id="free-trial-period"
                                    type="number"
                                    autoFocus
                                    hiddenLabel
                                    placeholder="Enter free trial period"
                                    fullWidth
                                    value={freeTrialPeriod}
                                    onChange={(e) => setFreeTrialPeriod(e.target.value)}
                                />
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsSettingsOpen(false)} disabled={isUpdatingSettings}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => updateSettings({ days: Number(freeTrialPeriod) })}
                        disabled={isUpdatingSettings || isLoadingSettings}
                    >
                        {isUpdatingSettings ? <CircularProgress size={20} color="inherit" /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Subscriptions;
